import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

import { CheckCircleIcon } from "@heroicons/react/24/outline";

import CustomButton from "./CustomButton";
import Capabilities from "./Capabilities";
import ServiceCategoryV2Dropdown from "./ServiceCategoryV2Dropdown";

import { UseGetRfiListForFilters } from "../custom-hooks/queries";

/**
 * The add-on picker is the platform V2 service-category control, with
 * the assessment's existing baseline categories excluded from the OPTIONS list (not
 * pre-filtering `rfiList`, which would break the V2 control's own parent-chain
 * taxonomy walks — see `ServiceCategoryV2Dropdown`'s `excludedValues` prop). No custom
 * scope tags are collected here — those are admin-only, added later via Assessment
 * Details edit after confirm.
 */
export default function JoinAssessmentConfirmationModal({
  open,
  setOpen,
  onConfirm,
  servicesScopes = [], // Provide default empty array
}) {
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [desiredInitialDraftDate, setDesiredInitialDraftDate] = useState("");
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  const { data: rfis } = UseGetRfiListForFilters();

  const baselineExcludedValues = (servicesScopes || [])
    .map((item) => item.rfi_id)
    .filter(Boolean);

  // Reset per-open state so a previous join attempt's selections never leak into the
  // next time this modal opens for a different (or the same) assessment.
  useEffect(() => {
    if (open) {
      setSelectedAddOns([]);
      setAdditionalInfo("");
      setDesiredInitialDraftDate("");
    }
  }, [open]);

  const handleJoin = () => {
    const additionalServices = selectedAddOns.map((option) => ({
      rfi_id: option.value,
      rfi_name: option.label,
    }));

    onConfirm(
      additionalInfo,
      additionalServices,
      desiredInitialDraftDate || null
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      className="relative z-50"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-opacity-40 backdrop-blur-sm backdrop-filter transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-center shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="flex flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>

              <DialogTitle
                as="h3"
                className="mt-3 text-base font-semibold leading-6 text-gray-900"
              >
                Join Assessment Confirmation
              </DialogTitle>

              <p className="mt-2 text-sm text-gray-500">
                Please provide any additional details.
              </p>

              <div className="w-full mt-4 text-left">
                <p className="block text-sm font-medium text-gray-700">
                  Current Assessment Scope
                </p>
                <div
                  data-testid="current-assessment-scope"
                  className="mt-1 rounded-md border border-gray-200 bg-gray-50 p-2"
                >
                  <Capabilities services={servicesScopes} icon={true} />
                </div>
              </div>

              <div className="w-full mt-4 text-left">
                <label
                  htmlFor="addOnServices"
                  className="block text-sm font-medium text-gray-700"
                >
                  Additional Services
                </label>
                <div className="mt-1">
                  <ServiceCategoryV2Dropdown
                    rfiList={rfis || []}
                    selected={selectedAddOns}
                    onChange={setSelectedAddOns}
                    excludedValues={baselineExcludedValues}
                    placeholder="Select add-on service categories"
                  />
                </div>
              </div>

              <div className="w-full mt-4 text-left">
                <label
                  htmlFor="desiredInitialDraftDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Desired Initial Draft Report Date
                </label>
                <input
                  id="desiredInitialDraftDate"
                  name="desiredInitialDraftDate"
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  value={desiredInitialDraftDate}
                  onChange={(e) => setDesiredInitialDraftDate(e.target.value)}
                />
              </div>

              <div className="w-full mt-4">
                <label
                  htmlFor="additionalInfo"
                  className="block text-sm font-medium text-gray-700"
                >
                  Additional Information
                </label>

                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter additional information here..."
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-5 sm:mt-4 flex justify-center space-x-3">
              <CustomButton
                text="Close"
                width="w-48"
                height="h-10"
                weight="font-semibold"
                size="text-md"
                color="bg-white"
                textColor="text-black"
                borderRadius="rounded-md"
                border="border border-gray-500"
                onClick={() => setOpen(false)}
              />

              <CustomButton
                text="Join"
                width="w-48"
                height="h-10"
                weight="font-semibold"
                size="text-md"
                color="bg-indigo-600"
                textColor="text-white"
                borderRadius="rounded-md"
                border="border border-blue-500"
                onClick={handleJoin}
              />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
